import { DOMAIN_NAME } from "../constants";

const SESSION_KEY = "ah_session_id";

const STEP_MAP: Record<string, number> = {
  page_view: 0,
  step_1_zip_check: 1,
  step_2_property_select: 2,
  step_3_pest_select: 3,
  outcome_reached: 4,
  phone_click_trigger: 5,
};

const pageLoadMs =
  typeof performance !== "undefined" && performance.timeOrigin ? performance.timeOrigin : Date.now();

function getSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const id = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, id);
    return id;
  } catch (err) {
    console.error("Analytics session error:", err);
    return crypto.randomUUID();
  }
}

function getDeviceProfile(): string {
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ? "mobile" : "desktop";
}

function getUserAgentSimplified(): string {
  const ua = navigator.userAgent;
  if (/bot|crawl|spider|slurp|googlebot|bingbot|yandex|baidu|duckduckbot/i.test(ua)) {
    return "bot";
  }
  return "human";
}

function getReferrerSource(): string {
  const ref = document.referrer;
  if (!ref) return "direct";
  try {
    const host = new URL(ref).hostname;
    if (/google\./i.test(host)) return "google";
    if (/bing\./i.test(host)) return "bing";
    if (/facebook\./i.test(host)) return "facebook";
    return host;
  } catch (err) {
    console.error("Analytics referrer parse error:", err);
    return "unknown";
  }
}

function getLoadLatencyMs(): number {
  if (typeof performance === "undefined") return 0;
  const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
  if (nav?.domInteractive) return Math.round(nav.domInteractive);
  return Math.round(performance.now());
}

function secondsSinceLoad(): number {
  return Math.round((Date.now() - pageLoadMs) / 1000);
}

function send(payload: Record<string, unknown>): void {
  const body = JSON.stringify(payload);
  const blob = new Blob([body], { type: "application/json" });
  if (navigator.sendBeacon?.("/api/stats", blob)) return;

  fetch("/api/stats", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch(err => {
    console.error("Analytics send failed:", err);
  });
}

function track(eventType: string, metadata: Record<string, unknown> = {}): void {
  if (!(eventType in STEP_MAP)) {
    console.error("Analytics unknown event:", eventType);
    return;
  }
  send({
    session_id: getSessionId(),
    domain_origin: DOMAIN_NAME,
    step: STEP_MAP[eventType],
    event_type: eventType,
    device_profile: getDeviceProfile(),
    user_agent_simplified: getUserAgentSimplified(),
    metadata,
  });
}

export function trackPageView(): void {
  track("page_view", {
    load_latency_ms: getLoadLatencyMs(),
    referrer_source: getReferrerSource(),
  });
}

export function trackZipCheck(zip: string, isValidGeo: boolean): void {
  track("step_1_zip_check", { zip_entered: zip, is_valid_geo: isValidGeo });
}

export function trackPropertySelect(propertyType: string): void {
  const normalized = propertyType === "commercial" ? "commercial" : "residential";
  track("step_2_property_select", { property_type: normalized });
}

export function trackPestSelect(pestCategory: string, isAllowed: boolean): void {
  track("step_3_pest_select", { pest_category: pestCategory, is_allowed_pest: isAllowed });
}

export function trackOutcome(qualificationStatus: boolean): void {
  track("outcome_reached", {
    qualification_status: qualificationStatus,
    duration_to_outcome_sec: secondsSinceLoad(),
  });
}

export function trackPhoneClick(): void {
  track("phone_click_trigger", { click_duration_from_view_sec: secondsSinceLoad() });
}
